import { ArticleCard, Box, Button, EventCard, Flex, Tag } from '@cowsquare/design';
import { Link } from 'gatsby';
import gql from 'graphql-tag';
import * as R from 'ramda';
import React from 'react';
import { Query } from 'react-apollo';
import { Helmet } from 'react-helmet';
import styled, { css } from 'styled-components';
import { getHTMLString } from '../../../../utils/getHTMLString';
import { useAdsQuery } from '../../../../utils/hooks';
import { isExpired } from '../../../../utils/isExpired';
import takeMap from '../../../../utils/takeMap';
import { Advertisements } from '../../../Advertisements';
import { getItemProps as getArticleCardProps } from '../../../ArticleCardList';
import { getItemProps as getEventCardProps } from '../../../EventCardList';
import { Section } from '../../../Section';
import { Title } from '../../../Title';

const ListFlex = styled(Flex)`
  & > :last-child {
    margin: 0;
  }
`;

const EventCardBox = styled(Box).attrs({
  mb: [5, 0],
  mr: [0, 5],
  width: [1, 1 / 3],
})`
  overflow: hidden;
  ${props =>
    props.expired &&
    css`
      opacity: 0.5;
    `}
`;

const GET_E_DRUG_HOME_DATA = gql`
  query getEDrugHomeData($userId: uuid) {
    events: hkdutest_e_drug_events(limit: 4, order_by: { date: desc }) {
      id
      name
      start_time
      end_time
      price
      date
      content
      location
      organizer {
        id
        name
      }
      doctors(where: { doctor: { user_id: { _eq: $userId } } }) {
        id
      }
      tags {
        tag {
          name
        }
      }
    }
    sales: hkdutest_e_drug_sales(limit: 6, order_by: { created_at: desc }) {
      id
      author {
        real_avatar
        name
        email
      }
      name
      e_drug_content {
        content
      }
      cover_file {
        link
      }
      tags {
        tag {
          name
        }
      }
      created_at
    }
    drugs: hkdutest_drugs(limit: 6) {
      id
      name
      cover {
        link
      }
      active_ingredients
      brand {
        name
      }
    }
  }
`;

const getDrugCardProps = item => ({
  height: '389px',
  width: [1, '320px'],
  description: R.pipe(R.propOr('', 'active_ingredients'), getHTMLString)(item),
  coverUrl: R.pathOr('', ['cover', 'link'], item),
  tags: R.pipe(R.pathOr('', ['brand', 'name']), name => <Tag key={name}>{name}</Tag>)(item),
});

const Home = ({ userId, uri }) => {
  const { ads } = useAdsQuery(uri);
  return (
    <Query
      query={GET_E_DRUG_HOME_DATA}
      variables={{
        userId,
      }}
    >
      {({ data }) => {
        const events = R.propOr([], 'events', data);
        const sales = R.propOr([], 'sales', data);
        const drugs = R.propOr([], 'drugs', data);

        return (
          <>
            <Helmet>
              <title>Home - HKDU E-Pharm</title>
            </Helmet>
            <Advertisements ads={ads} itemProps={{ height: ['150px', '300px'] }} />
            <Section>
              <Flex flexDirection="column">
                <Box px={[5, 0]} py={8}>
                  <Flex flexDirection="column">
                    <Box mb={8}>
                      <Box mb={6}>
                        <Flex justifyContent="space-between" alignItems="center">
                          <Title>Events</Title>
                          {R.gt(events.length, 3) && (
                            <Button size="small" as={Link} to="/e-pharm/events">
                              More
                            </Button>
                          )}
                        </Flex>
                      </Box>
                      <ListFlex flexDirection={['column', 'row']}>
                        {takeMap(3, item => (
                          <EventCardBox key={item.id} expired={isExpired(item.date)}>
                            <EventCard
                              as={Link}
                              to={`/e-pharm/events/${R.propOr('', 'id', item)}`}
                              {...getEventCardProps({
                                item,
                                uri: '/e-pharm/events',
                              })}
                              height="260px"
                              variant="vertical"
                            />
                          </EventCardBox>
                        ))(events)}
                      </ListFlex>
                    </Box>
                    <Flex flexDirection={['column', 'row']}>
                      {/* -------- Sales(Drug News)-------- */}
                      <Flex flexDirection="column" flex="1">
                        <Box mb={[8, 0]}>
                          <Box mb={6}>
                            <Flex justifyContent="space-between" alignItems="center">
                              <Title>Drug News</Title>
                              {R.gt(sales.length, 5) && (
                                <Button size="small" as={Link} to="/e-pharm/drug-news">
                                  More
                                </Button>
                              )}
                            </Flex>
                          </Box>
                          <ListFlex flexDirection="column">
                            {takeMap(5, item => (
                              <Box key={item.id} mb={5}>
                                <ArticleCard
                                  as={Link}
                                  to={`/e-pharm/drug-news/${R.propOr('', 'id', item)}`}
                                  {...getArticleCardProps({
                                    item,
                                    uri: '/e-pharm/drug-news',
                                  })}
                                  height={['389px', '246px']}
                                  description={R.pipe(
                                    R.pathOr('', ['e_drug_content', 'content']),
                                    getHTMLString
                                  )(item)}
                                />
                              </Box>
                            ))(sales)}
                          </ListFlex>
                        </Box>
                      </Flex>
                      {/* -------- Drug Search -------- */}
                      <Flex flexDirection="column">
                        <Box ml={[0, 5]}>
                          <Box mb={6}>
                            <Flex justifyContent="space-between" alignItems="center">
                              <Title>Drug Search</Title>
                              {R.gt(drugs.length, 5) && (
                                <Button size="small" as={Link} to="/e-pharm/drug-search">
                                  More
                                </Button>
                              )}
                            </Flex>
                          </Box>
                          <ListFlex flexDirection="column">
                            {takeMap(5, item => (
                              <Box key={item.id} mb={5}>
                                <EventCard
                                  as={Link}
                                  to={`/e-pharm/drug-search/${R.propOr('', 'id', item)}`}
                                  {...getEventCardProps({
                                    item,
                                    uri: '/e-pharm/drug-search',
                                  })}
                                  {...getDrugCardProps(item)}
                                />
                              </Box>
                            ))(drugs)}
                          </ListFlex>
                        </Box>
                      </Flex>
                    </Flex>
                  </Flex>
                </Box>
              </Flex>
            </Section>
          </>
        );
      }}
    </Query>
  );
};

export default Home;
